"use client";

import { useEffect, useState, type ComponentType, type ReactNode } from "react";
import {
  DEFAULT_ANONYMOUS_CAPABILITIES,
  type GeneratorCapabilities,
} from "../domain/capabilities";
import type { QrGeneratorSeed } from "../domain/seed";
import { useCurrentCapabilities } from "../state/use-current-capabilities";
import { QrGenerator, type QrGeneratorProps } from "./qr-generator";

/**
 * Bridges the marketing page (a server component) to the capability-aware
 * `<QrGenerator>` (ADR-023, Phase 1). Owns the OIDC bootstrap gate so
 * the `isLoggedIn` read inside the resolver hook is always safe, while
 * keeping the `<QrGenerator>` mounted continuously — form state survives
 * the brief window between "OIDC bootstrapping" and "real caps available",
 * which a naïve gate-around-the-tree pattern would discard.
 *
 * The default seed caps suppress the marketing CTAs during the bootstrap
 * window so logged-in users never see a flash of "Sign up" before their
 * session settles. Anonymous visitors get the CTAs the moment OIDC reports
 * a non-session — same beat as the previous `<HideWhenLoggedIn>` wrapper.
 *
 * `initialCaps` is the seed used until the first `useCurrentCapabilities`
 * tick — surfaces (e.g. `/codes/new`, ADR-023 Phase 2) that know the user
 * is authenticated pass the logged-in seed so the save button (rather
 * than the download button) renders during the bootstrap window.
 *
 * `Gate` is the consumer-supplied OIDC-initialization wrapper — the
 * package can't import `OidcInitializationGate` from web/'s auth layer
 * without coupling. Pass `OidcInitializationGate` from `@/lib/auth/oidc`
 * (or its equivalent) at the call site.
 *
 * `useIsLoggedIn` is the consumer-supplied auth hook called inside the
 * Gate — must not be called outside the gate (oidc-spa requirement).
 */
export interface QrGeneratorContainerProps
  extends Pick<QrGeneratorProps, "appUrl"> {
  initialCaps?: GeneratorCapabilities;
  /**
   * Pre-fill the editor from a draft (used by the anonymous → authenticated
   * bridge: a marketing-side state snapshot is consumed on `/codes/new`
   * after sign-in, so the user sees their typed code unchanged).
   */
  seed?: QrGeneratorSeed;
  /**
   * Wrapper that defers its children until the auth/OIDC layer has
   * settled. The inner `<CapabilityWatcher>` reads the auth state via
   * `useIsLoggedIn`, which is unsafe outside the gate.
   */
  Gate: ComponentType<{ fallback?: ReactNode; children: ReactNode }>;
  /** Reads the live auth state. Called only inside the Gate. */
  useIsLoggedIn: () => boolean;
}

export function QrGeneratorContainer({
  initialCaps,
  seed,
  appUrl,
  Gate,
  useIsLoggedIn,
}: QrGeneratorContainerProps) {
  const [caps, setCaps] = useState<GeneratorCapabilities>(
    initialCaps ?? MARKETING_BOOTSTRAP_CAPABILITIES,
  );

  return (
    <>
      <QrGenerator caps={caps} seed={seed} appUrl={appUrl} />
      <Gate fallback={null}>
        <CapabilityWatcher onChange={setCaps} useIsLoggedIn={useIsLoggedIn} />
      </Gate>
    </>
  );
}

const MARKETING_BOOTSTRAP_CAPABILITIES: GeneratorCapabilities = {
  ...DEFAULT_ANONYMOUS_CAPABILITIES,
  showAccountCTAs: false,
};

/**
 * Seed caps for surfaces that are guarded by `ProtectedRoute` — the user
 * is definitely authenticated, so the save button renders straight away
 * and no marketing CTAs appear during the OIDC bootstrap window. Mirrors
 * the conservative free-tier branch the resolver hook returns while the
 * `/v1/billing/plan` query is in flight.
 */
export const LOGGED_IN_BOOTSTRAP_CAPABILITIES: GeneratorCapabilities = {
  ...DEFAULT_ANONYMOUS_CAPABILITIES,
  showAccountCTAs: false,
  primaryAction: "save",
};

function CapabilityWatcher({
  onChange,
  useIsLoggedIn,
}: {
  onChange: (caps: GeneratorCapabilities) => void;
  useIsLoggedIn: () => boolean;
}) {
  const isLoggedIn = useIsLoggedIn();
  const caps = useCurrentCapabilities({ isLoggedIn });
  useEffect(() => {
    onChange(caps);
  }, [caps, onChange]);
  return null;
}
